import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, MessageCircle, Phone, FlaskConical, Utensils,
  Cloud, Wheat, Sparkles, Layers, Quote,
} from "lucide-react";
import { useSite, waLinkFromNumber } from "@/context/SiteContext";

const HERO_PILLS = [
  "Food Product Development",
  "Restaurant Consultancy",
  "Cloud Kitchen Consultancy",
  "Recipe Standardization",
  "Raw Material Supply",
  "Brand Development",
];

const SERVICES = [
  { icon: FlaskConical, title: "Product Development", to: "/product-development",
    desc: "New product R&D, formulation, shelf-life and manufacturing support." },
  { icon: Utensils, title: "Food Consultancy", to: "/food-consultancy",
    desc: "Menu engineering, food costing, SOPs and full kitchen consultancy." },
  { icon: Wheat, title: "Raw Material Supply", to: "/raw-material-supply",
    desc: "Masalas, marinades, gravies, sauces — B2B catalogue with WhatsApp RFQ." },
  { icon: Cloud, title: "Cloud Kitchen Setup", to: "/franchise-solutions",
    desc: "End-to-end cloud kitchen consultancy with FOCO model franchise solutions." },
  { icon: Sparkles, title: "Brand Launch Support", to: "/brands",
    desc: "Three in-house brands ready to franchise — Begum's, ROLLiT, Let's Chaap." },
  { icon: Layers, title: "Menu Engineering", to: "/food-consultancy",
    desc: "Data-driven menu design, costing and profitability optimisation." },
];

export default function Home() {
  const navigate = useNavigate();
  const { content } = useSite();
  const [quoteSubmitted, setQuoteSubmitted] = useState(false);

  return (
    <div data-testid="page-home" className="bg-cream">
      {/* Hero */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-[var(--k-olive-soft)] opacity-50 blur-[100px]" />
          <div className="absolute top-1/2 -left-40 w-[500px] h-[500px] rounded-full bg-[var(--k-sand)] opacity-60 blur-[120px]" />
          <div className="absolute inset-0 dotted-bg opacity-30" />
        </div>

        <div className="container-k px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            className="max-w-5xl"
          >
            <div className="font-mono-label text-[var(--k-green)] mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-[var(--k-olive)]" />
              KOSTRA Hospitality Pvt. Ltd.
            </div>
            <h1
              data-testid="home-headline"
              className="font-serif-display text-5xl md:text-7xl lg:text-[5.5rem] leading-[1.02] text-[var(--k-text)]"
            >
              India's complete{" "}
              <em className="green-gradient-text not-italic font-semibold">B2B food</em>{" "}
              ecosystem.
            </h1>
            <p
              data-testid="home-tagline"
              className="mt-7 font-serif-display italic text-2xl md:text-3xl text-[var(--k-text-2)] max-w-3xl leading-relaxed"
            >
              {content.tagline}
            </p>

            <div className="mt-10 flex flex-wrap gap-2 max-w-3xl" data-testid="home-pills">
              {HERO_PILLS.map((p, i) => (
                <motion.span
                  key={p}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 + i * 0.08 }}
                  className="px-4 py-2 border border-[var(--k-olive)] rounded-full font-mono-label text-[0.62rem] text-[var(--k-green)] bg-white/40 backdrop-blur"
                >
                  {p}
                </motion.span>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="home-cta-row">
              <button
                onClick={() => navigate("/contact")}
                data-testid="home-cta-consult"
                className="btn-green whitespace-nowrap"
              >
                Get Consultation
                <ArrowRight className="w-4 h-4" />
              </button>
              <Link
                to="/admin"
                data-testid="home-cta-admin"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[var(--k-line)] font-mono-label tracking-wider text-[0.62rem] text-[var(--k-text-2)] hover:text-[var(--k-green)] hover:border-[var(--k-green)] transition-colors whitespace-nowrap bg-white/40 backdrop-blur"
              >
                Admin Login
              </Link>
              <button
                onClick={() => {
                  navigate("/raw-material-supply");
                  setQuoteSubmitted(true);
                }}
                data-testid="home-cta-quote"
                className="btn-outline-green whitespace-nowrap"
              >
                Request Quote
              </button>
              <a
                href={waLinkFromNumber(content.whatsapp, "Hi KOSTRA — I have a query.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-cta-whatsapp"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#25D366] hover:bg-[#1ebd5a] text-white font-mono-label tracking-wider text-[0.7rem] transition-colors"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="section bg-beige" data-testid="home-services">
        <div className="container-k">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl mb-16"
          >
            <div className="font-mono-label text-[var(--k-gold)] mb-4">What we do</div>
            <h2 className="font-serif-display text-4xl md:text-6xl text-[var(--k-text)] leading-[1.05]">
              One partner. <em className="green-gradient-text not-italic">Six solutions.</em>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <motion.button
                key={s.title}
                onClick={() => navigate(s.to)}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="organic-card p-9 text-left group"
                data-testid={`home-service-${i}`}
              >
                <div className="flex items-start justify-between mb-7">
                  <div className="w-12 h-12 rounded-full bg-[var(--k-bg-2)] flex items-center justify-center border border-[var(--k-line)] group-hover:bg-[var(--k-green)] transition-colors duration-500">
                    <s.icon className="w-5 h-5 text-[var(--k-green)] group-hover:text-[var(--k-bg)] transition-colors" strokeWidth={1.4} />
                  </div>
                  <ArrowRight className="w-4 h-4 text-[var(--k-muted)] group-hover:text-[var(--k-green)] group-hover:translate-x-1 transition-all" />
                </div>
                <h3 className="font-serif-display text-2xl text-[var(--k-text)] mb-2 leading-tight">
                  {s.title}
                </h3>
                <p className="text-[var(--k-text-2)] text-sm leading-relaxed">{s.desc}</p>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Why us strip */}
      <section className="section bg-cream" data-testid="home-why">
        <div className="container-k grid lg:grid-cols-12 gap-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <div className="font-mono-label text-[var(--k-gold)] mb-4">Why KOSTRA</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[var(--k-text)] leading-[1.05]">
              We are the operating <em className="green-gradient-text not-italic">spine</em> behind India's modern kitchens.
            </h2>
            <p className="mt-6 text-[var(--k-text-2)] leading-relaxed max-w-md">
              From product R&amp;D and recipe standardisation to franchise-grade
              SOPs and B2B raw material supply — KOSTRA stitches the entire
              food business value chain into one partner.
            </p>
            <button
              onClick={() => navigate("/about")}
              data-testid="home-about-btn"
              className="mt-10 btn-outline-green"
            >
              Learn More <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-7 grid sm:grid-cols-2 gap-5"
          >
            {[
              ["End-to-End", "Product development to scale supply."],
              ["B2B Native", "Built for restaurants, cloud kitchens, hotels and QSR chains."],
              ["SOP Driven", "Every workflow codified, every kitchen consistent."],
              ["Brand-Ready", "Three turnkey brands available for franchise."],
            ].map(([t, d]) => (
              <div key={t} className="organic-card p-7">
                <div className="font-serif-display text-2xl text-[var(--k-green)] mb-2">{t}</div>
                <p className="text-[var(--k-text-2)] text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Quote CTA */}
      <section className="section bg-sand" data-testid="home-quote-cta">
        <div className="container-k">
          <div className="organic-card p-10 md:p-16 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <Quote className="w-8 h-8 text-[var(--k-olive)] mb-5" strokeWidth={1.2} />
              <h3 className="font-serif-display text-3xl md:text-4xl text-[var(--k-text)] leading-tight">
                Let's build a profitable food business — together.
              </h3>
              <p className="mt-4 text-[var(--k-text-2)] leading-relaxed max-w-2xl">
                Tell us what you're scaling. We'll respond within 24 hours
                with a tailored plan.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3">
              <button
                onClick={() => navigate("/contact")}
                className="btn-green w-full"
                data-testid="home-quote-consult"
              >
                Get Consultation <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href={waLinkFromNumber(content.whatsapp, "Hi KOSTRA — let's connect.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-quote-whatsapp"
                className="inline-flex justify-center items-center gap-2 w-full bg-[#25D366] hover:bg-[#1ebd5a] text-white py-3.5 rounded-full font-mono-label tracking-wider text-[0.72rem] transition-colors"
              >
                <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
              </a>
              <a
                href={`tel:${content.whatsapp}`}
                className="inline-flex justify-center items-center gap-2 w-full border border-[var(--k-line)] py-3 rounded-full font-mono-label tracking-wider text-[0.7rem] text-[var(--k-text)] hover:border-[var(--k-green)] transition-colors"
                data-testid="home-quote-call"
              >
                <Phone className="w-4 h-4" /> Call Sales
              </a>
            </div>
          </div>
        </div>
        {quoteSubmitted && (
          <div className="sr-only" data-testid="home-quote-sent">redirecting</div>
        )}
      </section>
    </div>
  );
}
